import React, { useState } from 'react';
import _sortBy from 'lodash/sortBy';

import Box from '@mui/material/Box';
import Button from '@components/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';

import InlineLoadingIndicator from '@common/components/loading-indicator/inline-loading-indicator';
import { useLoadLockdownBrowserAllowlist } from './ldb-allowlist-data-hooks';
import LockdownBrowserAllowlistItem from './ldb-allowlist-list-item';
import AddAllowlistItemPanel from './add-ldb-allowlist-item';

const CompatWarning = () => (
  <Box sx={{ p: 1, my: 1, border: 1, borderColor: 'grey.400' }}>
    <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
      NOTICE: Respondus has announced that version 2.1.3.08 is the final version that
      supports 32-bit Windows operating systems.
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
      <WarningIcon color="warning" />
      <Typography variant="caption" color="warning">
        Admin Action Required: Please verify that district devices meet 64-bit compatibility
        requirements for future updates. Devices running 32-bit Windows (includes Windows 7
        and some Windows 10 installs) will no longer be supported in subsequent releases.
      </Typography>
    </Box>
  </Box>
);

const ConfigureLockdownBrowserView = () => {
  const { lockdownBrowserAllowlist, loading, error } = useLoadLockdownBrowserAllowlist();

  const [isAddingItem, setIsAddingItem] = useState(false);

  const sortedAllowlist = _sortBy(lockdownBrowserAllowlist, ['program']);
  return (
    <Container maxWidth="md" sx={{ p: 3 }}>
      <Typography variant="pageSection" gutterBottom sx={{ mb: 1 }}>
        LockDown Browser Allowlist
      </Typography>

      <Typography gutterBottom sx={{ mb: 1 }}>
        To add an item to the LockDown Browser Allowlist, click the &ldquo;Add New Allowlist
        Item&rdquo; below and enter the name of the process you would like to add.
      </Typography>

      <Typography gutterBottom sx={{ mb: 1 }}>
        To get the process name in Windows 10, launch the application that you would like to
        add to the Allowlist, then{' '}
        <Link
          href="https://www.digitalcitizen.life/task-manager-details"
          target="_blank"
          rel="noopener"
          underline="hover"
        >
          launch the Task Manager
        </Link>{' '}
        and use the text from the &ldquo;Name&rdquo; column in the &ldquo;Details&rdquo;
        tab. For example, to allow Zoom on Windows, enter &ldquo;zoom.exe&rdquo;.
      </Typography>

      <Typography gutterBottom sx={{ mb: 1 }}>
        For MacOS, launch the application you would like to add an item to the Allowlist,
        then open{' '}
        <Link
          href="https://support.apple.com/guide/activity-monitor/view-information-about-processes-actmntr1001/mac"
          target="_blank"
          rel="noopener"
          underline="hover"
        >
          Activity Monitor
        </Link>{' '}
        and use the text in the &ldquo;Process Name&rdquo; column with a &ldquo;.app&rdquo;
        suffix. For example, to allow Zoom on MacOS, enter &ldquo;us.zoom.app&rdquo;.
      </Typography>

      <Typography gutterBottom sx={{ mb: 1 }}>
        To allow a program across both operating systems, you must make two separate
        Allowlist entries. For more information, see the{' '}
        <Link
          href="https://support.eduphoria.net/docs/lockdown-browser-allowlist-options"
          target="_blank"
          rel="noopener"
          underline="hover"
        >
          LockDown Browser Allowlist help article
        </Link>
        .
      </Typography>

      <Typography gutterBottom sx={{ mb: 1 }}>
        To add a Chrome plugin, insert the extension ID with the prefix
        &ldquo;CB-EXT:&rdquo;.
      </Typography>

      <Typography gutterBottom sx={{ mb: 1 }}>
        To install the managed LockDown Browser Chrome extension, see the{' '}
        <Link
          href="https://support.eduphoria.net/docs/lockdown-browser-for-chromebooks-chrome-extension-administrator-guide"
          target="_blank"
          rel="noopener"
          underline="hover"
        >
          LockDown Browser for Chromebooks help article
        </Link>
        .
      </Typography>

      <Divider sx={{ my: 3 }} />

      {loading ? (
        <InlineLoadingIndicator />
      ) : error ? (
        <Box sx={{ my: 3 }}>
          <Typography variant="body2" gutterBottom>
            There was an error loading the Allowlist.
          </Typography>
        </Box>
      ) : (
        <>
          <List dense>
            {sortedAllowlist.map(allowlistItem => (
              <LockdownBrowserAllowlistItem
                key={allowlistItem.id}
                isAddingItem={isAddingItem}
                allowlistItemId={allowlistItem.id}
                allowlistItemProgram={allowlistItem.program}
              />
            ))}
          </List>

          {!isAddingItem ? (
            <>
              <Button
                variant="outlined"
                disabled={lockdownBrowserAllowlist.length >= 10}
                startIcon={<PlaylistAddIcon />}
                onClick={() => setIsAddingItem(true)}
              >
                Add New Allowlist Item
              </Button>
              {lockdownBrowserAllowlist.length >= 10 && (
                <FormHelperText>
                  LockDown Browser Allowlist is limited to 10 items
                </FormHelperText>
              )}
            </>
          ) : (
            <AddAllowlistItemPanel
              setIsAddingItem={setIsAddingItem}
              list={lockdownBrowserAllowlist}
            />
          )}
        </>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant="pageSection" gutterBottom>
        Lab Installer Links
      </Typography>
      <List dense>
        <ListItem
          disableGutters
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <ListItemText>
            <Link
              href="https://static.schoolobjects.com/ldb/2026-03/LockDownBrowserOEMSetup.exe"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Windows - Student Edition (requires admin rights)
            </Link>
          </ListItemText>
          <Typography variant="caption" color="textSecondary">
            Last Updated: Mar 19, 2026 (Version 2.1.3.09)
          </Typography>

          <CompatWarning />
        </ListItem>

        <ListItem
          disableGutters
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <ListItemText secondary="Note: In December of 2022, Respondus deprecated the .exe version of LDB for Labs. Only the MSI is needed. If you have a previous .exe version installed, please fully uninstall that version prior to completing this update.">
            <Link
              href="https://static.schoolobjects.com/ldb/2026-03/LockDownBrowserLabOEM-MSI.zip"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Windows - Lab Edition (MSI installer for managed installs)
            </Link>
          </ListItemText>
          <Typography variant="caption" color="textSecondary">
            Last Updated: Mar 19, 2026 (Version 2.1.3.09)
          </Typography>

          <CompatWarning />
        </ListItem>

        <ListItem
          disableGutters
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <ListItemText>
            <Link
              href="https://static.schoolobjects.com/ldb/2026-03a/InstallLDBOEM.zip"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              macOS - Student and Lab use
            </Link>
          </ListItemText>
          <Typography variant="caption" color="textSecondary">
            Last Updated: Mar 27, 2026 (Version 2.1.5.02)
          </Typography>
        </ListItem>
      </List>
    </Container>
  );
};

export default ConfigureLockdownBrowserView;
